
import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { useGame } from '../../contexts/GameContext';
import { Award, Clock, BarChart } from "lucide-react";

const SimulationStatus: React.FC = () => {
  const { 
    selectedRole, 
    careerLevel, 
    yearsPassed, 
    experience, 
    achievements,
    decisionHistory
  } = useGame();
  
  if (!selectedRole) return null;
  
  // Calculate overall progress
  const totalLevels = selectedRole.careerPath.length;
  const currentLevelIndex = selectedRole.careerPath.findIndex(path => path.level === careerLevel);
  const isMaxLevel = currentLevelIndex === totalLevels - 1;
  
  // Calculate overall career progress (based on levels completed)
  const careerProgress = ((currentLevelIndex + 1) / totalLevels) * 100;
  
  // Calculate decisions impact
  const totalDecisions = decisionHistory.length;
  const decisionsPerYear = totalDecisions > 0 ? (totalDecisions / yearsPassed).toFixed(1) : '0';
  
  // Estimate remaining time
  const remainingLevels = totalLevels - (currentLevelIndex + 1);
  const averageYearsPerLevel = yearsPassed > 0 ? yearsPassed / (currentLevelIndex + 1) : 3;
  const estimatedRemainingYears = isMaxLevel ? 0 : Math.round(remainingLevels * averageYearsPerLevel);
  
  // Check if simulation is complete (reached max level)
  const simulationComplete = isMaxLevel;

  return (
    <Card className="bg-web3-blue/10 border-web3-blue/20">
      <CardHeader>
        <CardTitle className="flex items-center">
          <BarChart className="mr-2 text-web3-green" /> Simulation Status
        </CardTitle>
        <CardDescription>Your progress through the career simulation</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="space-y-6">
          {simulationComplete && (
            <div className="bg-web3-green/10 border border-web3-green/20 rounded-md p-4 text-center">
              <Award className="mx-auto h-10 w-10 text-web3-green mb-2" />
              <h3 className="text-xl font-bold text-web3-green mb-1">Simulation Complete!</h3>
              <p className="text-white/80">
                Congratulations! You've reached the highest level in your career path.
              </p>
              <p className="text-white/80 mt-2">
                You can continue exploring or start a new career path.
              </p>
            </div>
          )}
          
          <div>
            <h3 className="text-md font-medium text-white mb-2">Overall Career Progress</h3>
            <div className="flex items-center">
              <div className="flex-1">
                <div className="w-full bg-muted rounded-full h-3">
                  <div 
                    className="bg-web3-green h-3 rounded-full" 
                    style={{ width: `${careerProgress}%` }}
                  ></div>
                </div>
              </div>
              <span className="ml-3 text-sm font-medium">{Math.round(careerProgress)}%</span>
            </div>
            <div className="flex justify-between mt-2 text-xs text-white/60">
              <span>Junior</span>
              <span>Mid-Level</span>
              <span>Senior</span>
              <span>Leader</span>
            </div>
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div className="p-3 bg-web3-blue/20 rounded-md">
              <div className="flex items-start">
                <Clock className="h-5 w-5 text-web3-green mr-2" />
                <div>
                  <div className="text-sm font-medium text-white">Years Passed</div>
                  <div className="text-2xl font-bold">{yearsPassed}</div>
                  {!simulationComplete && estimatedRemainingYears > 0 && (
                    <div className="text-xs text-white/60 mt-1">
                      Est. {estimatedRemainingYears} years remaining
                    </div>
                  )}
                </div>
              </div>
            </div>
            
            <div className="p-3 bg-web3-blue/20 rounded-md">
              <div className="flex items-start">
                <Award className="h-5 w-5 text-web3-green mr-2" />
                <div>
                  <div className="text-sm font-medium text-white">Achievements</div>
                  <div className="text-2xl font-bold">{achievements.length}</div>
                </div>
              </div>
            </div>
          </div>
          
          <div>
            <h3 className="text-md font-medium text-white mb-3">Simulation Statistics</h3>
            <ul className="space-y-2 text-sm">
              <li className="flex justify-between">
                <span className="text-white/80">Current Role:</span>
                <span className="font-medium text-white">
                  {selectedRole.careerPath.find(p => p.level === careerLevel)?.title}
                </span>
              </li>
              <li className="flex justify-between">
                <span className="text-white/80">Experience Points:</span>
                <span className="font-medium text-white">{experience} XP</span>
              </li>
              <li className="flex justify-between">
                <span className="text-white/80">Decisions Made:</span>
                <span className="font-medium text-white">{totalDecisions}</span>
              </li>
              <li className="flex justify-between">
                <span className="text-white/80">Decisions Per Year:</span>
                <span className="font-medium text-white">{decisionsPerYear}</span>
              </li>
              <li className="flex justify-between">
                <span className="text-white/80">Career Level:</span>
                <span className="font-medium text-white">{careerLevel}</span>
              </li>
            </ul>
          </div>
          
          {simulationComplete ? (
            <div className="bg-web3-blue/20 p-3 rounded-md text-sm text-white/80">
              <p>
                You've reached the highest level in your career path! You can:
              </p>
              <ul className="list-disc list-inside mt-2">
                <li>Continue in sandbox mode to improve your KPIs</li>
                <li>Start a new career in a different role</li>
                <li>Show off your achievements to others</li>
              </ul>
            </div>
          ) : (
            <div className="bg-web3-blue/20 p-3 rounded-md text-sm text-white/80">
              <p>
                To advance in the simulation, focus on:
              </p>
              <ul className="list-disc list-inside mt-2">
                <li>Improving your KPIs through strategic decisions</li>
                <li>Gaining experience by facing challenges</li>
                <li>Unlocking skills that match your career objectives</li>
                <li>Meeting the time and experience requirements for promotion</li>
              </ul>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default SimulationStatus;
